import { ClickableText } from '@bitstopco/bitstop-theme';
import { Typography } from '@mui/material';

import { OrderDetails, OrderInventory } from '@/components/restocking/load-order';
import BillsTable from '@/components/restocking/tableColumns/BillsTable';
import MachineLoadStatus from '@/components/restocking/tableColumns/MachineLoadStatus';
import RestockingLoadOrderDotsMenu from '@/components/restocking/tableColumns/RestockingLoadOrderMenu';
import RestockingMachineDotsMenu from '@/components/restocking/tableColumns/RestockingMachineDotsMenu';
import RestockingStatus from '@/components/restocking/tableColumns/RestockingStatus';
import LoadOrders from '@/components/restocking/tabs/load-orders/LoadOrders';
import RestockingMachines from '@/components/restocking/tabs/machines/RestockingMachines';
import TableColumnClickableText from '@/components/ui/table/TableColumnClickableText';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import TableColumnIncludedLocation from '@/components/ui/table/TableColumnIncludedLocation';
import TableColumnMachine from '@/components/ui/table/TableColumnMachine';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckIcon from '@mui/icons-material/TaskAlt';
import MachineLowIcon from '@mui/icons-material/WarningOutlined';

import type { Machine } from '@/types';
import type { RestockRequestView } from '@/types/generated/athena';
import {
  ResidualBagType,
  RestockRequestStatus,
  RestockRequestType,
} from '@/types/generated/athena';
import type { Columns, TableToolbarFilterConfig } from '@bitstopco/bitstop-theme';

export const RESTOCKING_STATUS_LABELS = {
  [RestockRequestStatus.Created]: 'Created',
  [RestockRequestStatus.Fulfilled]: 'Fulfilled',
  [RestockRequestStatus.Loaded]: 'Loaded',
  [RestockRequestStatus.PendingReconcile]: 'Pending Reconciliation',
  [RestockRequestStatus.Completed]: 'Completed',
  [RestockRequestStatus.Cancelled]: 'Cancelled',
} as const;

export const RESTOCK_TYPE_LABELS = {
  [RestockRequestType.Add]: 'Add',
  [RestockRequestType.Swap]: 'Swap',
} as const;

export const PANELS = ['Machines', 'Load Orders'];
export const PANELS_ROUTES = ['/restocking', '/restocking/load-orders'];
export const PANELS_COMPONENTS = [RestockingMachines, LoadOrders];
export const RESIDUAL_BAG_LABELS: Record<ResidualBagType, string> = {
  [ResidualBagType.RejectBinOnly]: 'REJECTS',
  [ResidualBagType.CassetteOnly]: 'RESIDUALS',
  [ResidualBagType.RejectBinAndCassette]: 'REJECTS & RESIDUALS',
};

export const LOAD_ORDER_PANELS = ['Order Details', 'Inventory'];
export const LOAD_ORDER_PANELS_ROUTES = [
  '/restocking/load-orders/:id',
  '/restocking/load-orders/:id/inventory',
];
export const LOAD_ORDER_PANELS_COMPONENTS = [OrderDetails, OrderInventory];

export const RESTOCKING_MACHINES_COLUMNS: Columns<Machine> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'machine',
      valueMapper: (_, data) => <TableColumnMachine data={data} link />,
      valueSx: {
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'location_id',
      label: 'location',
      valueMapper: (_, data) => <TableColumnIncludedLocation link data={data} />,
    },
    {
      label: 'inventory',
      valueSx: { minWidth: 540, maxWidth: 540, pr: 4 },
      valueMapper: (_, { note_dispenser_cassettes }) => (
        <BillsTable cassettes={note_dispenser_cassettes ?? []} />
      ),
    },
    {
      value: 'is_bill_dispenser_low',
      label: 'load status',
      valueMapper: (value) => <MachineLoadStatus value={value} />,
    },
    {
      valueMapper: (_, machine) => <RestockingMachineDotsMenu machine={machine} />,
    },
  ],
};

export const RESTOCKING_MACHINES_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'id',
    type: 'select-ids',
    placeholder: 'Machine',
    inputPlaceholder: 'Machine ID',
    label: 'Machine ID',
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    inputPlaceholder: 'Location ID',
    label: 'Location ID',
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
  {
    type: 'select',
    property: 'is_bill_dispenser_low',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Load Status',
    clearText: 'Clear filter',
    label: 'Status',
    options: [
      { value: true as unknown as string, label: 'Low' },
      { value: false as unknown as string, label: 'Ok' },
    ],
    multiple: true,
  },
];

export const RESTOCKING_LOAD_ORDERS_COLUMNS: Columns<RestockRequestView> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'order id',
      valueMapper: (value) =>
        value && (
          <ClickableText
            text={value}
            clipboardValue={value}
            name="Load Order ID"
            truncate={{
              twoSegments: true,
            }}
          />
        ),
    },
    {
      value: 'access_code',
      label: 'code',
      valueMapper: (value) => (
        <Typography fontWeight="bold" fontFamily="monospace" variant="body2">
          {value.replace(/(.{4})/g, '$1-').replace(/-$/, '')}
        </Typography>
      ),
    },
    {
      value: 'restock_type',
      label: 'type',
      valueMapper: (value) => value.charAt(0).toUpperCase() + value.slice(1),
    },
    {
      value: 'atm_id',
      label: 'machine',
      valueMapper: (value, data) => (
        <TableColumnClickableText
          id={value}
          to={`/machine/${value}`}
          text={data.atm?.slug ?? `Machine ${value}`}
          fixIcon={true}
          clipboardValue={value}
          name="Machine ID"
          overrideTooltipLinkText="Go to Machine"
        />
      ),
    },
    {
      value: 'location_id',
      label: 'location',
      valueMapper: (value, data) => (
        <TableColumnClickableText
          id={value}
          to={`/location/${value}`}
          text={data.location?.location_name ?? `Location ${value}`}
          fixIcon={true}
          clipboardValue={value}
          name="Location ID"
          overrideTooltipLinkText="Go to Location"
        />
      ),
    },
    {
      value: 'status',
      label: 'status',
      valueMapper: (value) => <RestockingStatus status={value} />,
    },
    {
      label: 'load',
      valueSx: { minWidth: 540, maxWidth: 540, pr: 4 },
      valueMapper: (_, { cassettes }) => <BillsTable cassettes={cassettes ?? []} />,
    },
    {
      value: 'created_at',
      label: 'created at',
      valueMapper: (value) => (
        <TableColumnDate
          value={value}
          options={{
            timeZone: 'UTC',
          }}
          auxiliaryDate={{
            value: value,
            options: {
              normalizeAsUTC: true,
              performUtcToZonedTime: true,
            },
          }}
        />
      ),
    },
    {
      value: 'last_updated_at',
      label: 'last updated at',
      valueMapper: (value) => (
        <TableColumnDate
          value={value}
          options={{
            timeZone: 'UTC',
          }}
          auxiliaryDate={{
            value: value,
            options: {
              normalizeAsUTC: true,
              performUtcToZonedTime: true,
            },
          }}
        />
      ),
    },
    {
      valueMapper: (_, data) => <RestockingLoadOrderDotsMenu data={data} />,
    },
  ],
};

export const RESTOCKING_LOAD_ORDERS_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'id',
    type: 'select-ids',
    placeholder: 'Order ID',
    inputPlaceholder: 'Load Order ID',
    label: 'Load Order ID',
    mapperSelectedValue: (value: string) => `ID ${value}`,
    mapperSelectedValues: (count: number) => `Load Order ID (${count})`,
  },
  {
    type: 'select',
    property: 'restock_type',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Type',
    clearText: 'Clear filter',
    label: 'Type',
    options: [
      { value: RestockRequestType.Swap, label: 'Swap' },
      { value: RestockRequestType.Add, label: 'Add' },
    ],
    multiple: true,
  },
  {
    property: 'atm_id',
    type: 'select-ids',
    placeholder: 'Machine',
    inputPlaceholder: 'Machine ID',
    label: 'Machine ID',
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    inputPlaceholder: 'Location ID',
    label: 'Location ID',
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
  {
    type: 'select',
    property: 'status',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Status',
    clearText: 'Clear filter',
    label: 'Status',
    options: [
      { value: RestockRequestStatus.Created, label: 'Created' },
      { value: RestockRequestStatus.Fulfilled, label: 'Fulfilled' },
      { value: RestockRequestStatus.Loaded, label: 'Loaded' },
      { value: RestockRequestStatus.PendingReconcile, label: 'Pending Reconciliation' },
      { value: RestockRequestStatus.Completed, label: 'Completed' },
      { value: RestockRequestStatus.Cancelled, label: 'Cancelled' },
    ],
    multiple: true,
  },
];

export const LOAD_ORDER_DETAILS: Columns<RestockRequestView> = {
  defaultValue: '-',
  valueSx: {
    textAlign: 'right',
  },
  items: [
    {
      label: 'Load Order ID',
      value: 'id',
      valueMapper: (value) => (
        <ClickableText
          text={String(value)}
          clipboardValue={String(value)}
          name="Load Order ID"
          fixIcon
        />
      ),
    },
    {
      label: 'Load Type',
      value: 'restock_type',
      valueMapper: (value) => RESTOCK_TYPE_LABELS[value],
    },
    {
      label: 'Bag ID',
      value: 'bag_id',
      valueMapper: (value) => (
        <ClickableText name="Bag ID" text={value} clipboardValue={value} fixIcon={true} />
      ),
    },
    {
      label: 'Machine',
      value: 'atm_id',
      valueMapper: (value, data) => (
        <TableColumnClickableText
          id={String(data.atm_id)}
          to={`/machine/${value}`}
          text={data.atm?.slug ?? '-'}
          fixIcon={true}
          clipboardValue={String(value)}
          name="Machine ID"
          overrideTooltipLinkText="Go to Machine"
        />
      ),
    },
    {
      label: 'Location',
      value: 'location_id',
      valueMapper: (value, data) => (
        <TableColumnClickableText
          id={String(value)}
          to={`/location/${value}`}
          text={data.location?.location_name?.toString() ?? '-'}
          fixIcon={true}
          clipboardValue={String(value)}
          name="Location ID"
          overrideTooltipLinkText="Go to Location"
        />
      ),
    },
    {
      label: 'Last updated UTC Date',
      value: 'last_updated_at',
      valueMapper: (value) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
          />
        ),
    },
    {
      label: 'Last updated Local Date',
      value: 'last_updated_at',
      valueMapper: (value) => value && <TableColumnDate value={value} />,
    },
    {
      label: 'Created UTC Date',
      value: 'created_at',
      valueMapper: (value) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
          />
        ),
    },
    {
      label: 'Created Local Date',
      value: 'created_at',
      valueMapper: (value) => value && <TableColumnDate value={value} />,
    },
  ],
};

export const RESTOCKING_CARDS = {
  low_machines: {
    title: 'Low Machines',
    icon: MachineLowIcon,
    showFloatingIcon: false,
  },
  pending: {
    title: 'Pending Orders',
    icon: PendingActionsIcon,
    showFloatingIcon: false,
  },
  completed: {
    title: 'Completed Orders',
    icon: CheckIcon,
    showFloatingIcon: false,
  },
  total_load: {
    title: 'Total Loaded',
    icon: AttachMoneyIcon,
    showFloatingIcon: false,
  },
};
